
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Navigation, ScanEye, Maximize2, CreditCard, DollarSign, Loader2, Radio, Clock, ShoppingBasket, ChevronDown, PhoneOff, Phone, Copy, HelpCircle, Info, Headphones, MessageCircle, Send, BrainCircuit, Zap, CheckCircle2 } from 'lucide-react';
import { Order, SystemConfig } from '../../../types';
import { TapToConfirm } from '../../TapToConfirm';
import { toast } from 'react-hot-toast';
import { getStreetViewImageUrl } from '../../../services/reconService';
import { handleIFoodExternalConfirmation, openWhatsAppText } from '../../../services/communicationService';
import { getIfoodCommunicationId, requiresIfoodDeliveryValidation } from '../../../services/driverDeliveryConfirmation';

interface Props {
    order: Order;
    config: SystemConfig;
    isNext: boolean;
    etaMinutes: number;
    isNearDestination: boolean;
    isProcessingHailing: boolean;
    onCompleteOrder: (id: string, options?: { completedAt?: number; skipPerformanceIncrement?: boolean; skipExternalNotify?: boolean }) => void;
    onArrived?: (id: string) => void;
    onStartHailing: (order: Order) => void;
    onReconClick?: () => void;
    onStartInternalNav?: () => void;
    isInternalNavActive?: boolean;
}

export const ActiveMissionCard: React.FC<Props> = ({
    order, config, isNext, isNearDestination, isProcessingHailing, onCompleteOrder, onArrived, onStartHailing, onReconClick,
    onStartInternalNav, isInternalNavActive = false
}) => {
    const [showItems, setShowItems] = useState(false);
    const [reconUrl, setReconUrl] = useState<string | undefined>(undefined);
    const [isLoadingRecon, setIsLoadingRecon] = useState(false);
    const [showIfoodConfirmation, setShowIfoodConfirmation] = useState(false);

    // v41.7: Friendly ID Display
    const titleId = order.displayId ? `#${order.displayId}` : (order.shortCode ? `#${order.shortCode}` : `#${order.id.slice(-4)}`);
    const secondaryId = order.shortCode && order.shortCode !== order.displayId ? `#${order.shortCode}` : null;

    const handleWaze = (lat: number | undefined, lng: number | undefined) => { if (lat && lng) window.open(`https://waze.com/ul?ll=${lat},${lng}&navigate=yes`, '_blank'); };
    const handleMaps = (lat: number | undefined, lng: number | undefined, address: string) => { if (lat && lng) window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank'); else window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`, '_blank'); };

    const isPaid = (order.paymentMethod || '').toLowerCase().includes('pago') || (order.paymentMethod || '').toLowerCase().includes('online');
    const showPaymentInfo = config.driversCollectPayments !== false;
    const isIfoodSystem = requiresIfoodDeliveryValidation(order);
    const communicationId = getIfoodCommunicationId(order);
    const hasCommunicationId = /^\d{8}$/.test(communicationId);

    // v60.5: Calcula previsão de entrega com margem de 10 minutos APÓS o prazo
    // O prazo é o horário base, e a tolerância é adicionada (ex: prazo 19:20 -> previsão 19:20-19:30)
    const calculateDeliveryWindow = () => {
        if (!order.createdAt || !order.timeLimit) return null;
        const deadlineMs = order.createdAt + (order.timeLimit * 60 * 1000);
        const marginMinutes = 10;
        const startMs = deadlineMs;  // Início = prazo do pedido
        const endMs = deadlineMs + (marginMinutes * 60 * 1000);  // Fim = prazo + tolerância
        const formatTime = (ms: number) => {
            const date = new Date(ms);
            return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', hour12: false });
        };
        return {
            start: formatTime(startMs),
            end: formatTime(endMs),
            startMs,
            endMs
        };
    };
    const deliveryWindow = calculateDeliveryWindow();

    // v7.5: LAZY LOADING DE FACHADA
    const handleLoadFachada = async () => {
        if (reconUrl) return onReconClick?.();

        setIsLoadingRecon(true);
        try {
            const url = getStreetViewImageUrl(
                order.reconLat || order.lat || 0,
                order.reconLng || order.lng || 0,
                config.googleMapsApiKey,
                config.streetViewHeading,
                order.streetViewPanoId
            );
            if (url) {
                setReconUrl(url);
                toast.success("Fachada identificada!");
            } else {
                toast.error("Fachada indisponível para este local.");
            }
        } catch (e) {
            toast.error("Erro ao carregar fachada.");
        } finally {
            setIsLoadingRecon(false);
        }
    };

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copiado!`);
    };

    const handleCall = () => {
        if (isIfoodSystem) {
            // Se for iFood, oferece a central proxy
            const proxyPhone = order.customerProxyPhone || '0800 705 1020';
            const confirmCall = window.confirm(`Deseja ligar para a Central iFood (${proxyPhone})?\nO ID do pedido será copiado para facilitar.`);
            if (confirmCall) {
                if (hasCommunicationId) navigator.clipboard.writeText(communicationId);
                const cleanProxy = proxyPhone.replace(/\D/g, '');
                window.open(`tel:${cleanProxy}`);
                return;
            }
        }
        if (!order.phone) return;
        const cleanPhone = order.phone.replace(/\D/g, '');
        window.open(`tel:${cleanPhone}`);
    };

    const handleWhatsApp = () => {
        if (!order.phone) {
            toast.error('Telefone do cliente não encontrado.');
            return;
        }
        openWhatsAppText(order.phone, 'Opa! Aqui é o entregador da Cookie Cakau');
    };

    return (
        <motion.div className={`bg-card-bg/70 backdrop-blur-xl rounded-[2.5rem] shadow-xl border transition-all duration-300 overflow-hidden ${isNext ? 'border-sal-cyber-teal ring-1 ring-sal-cyber-teal/20 shadow-[0_0_30px_-10px_rgba(45,212,191,0.3)]' : 'border-border-dim opacity-80'}`}>
            <div className="bg-black/5 dark:bg-black/30 px-4 py-3 border-b border-border-dim">
                {/* Linha 1: IDs e badges principais */}
                <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="font-mono font-black text-sal-cyber-teal text-base">{titleId}</span>
                    {secondaryId && <span className="text-xs font-mono text-text-muted opacity-60">iFood {secondaryId}</span>}
                    <span className={`text-xs font-black px-2 py-0.5 rounded-full uppercase tracking-widest shrink-0 ${isIfoodSystem ? 'bg-status-critical text-badge-text' : 'bg-status-info text-badge-text'}`}>
                        {order.salesChannel || 'Loja'}
                    </span>
                    {order.arrivedAt && isNext && (
                        <span className="text-xs font-black px-2 py-0.5 rounded-full uppercase bg-status-ready text-badge-text animate-pulse shadow-[0_0_10px_rgba(var(--status-ready),0.5)] shrink-0">NO LOCAL</span>
                    )}
                </div>
                {/* Linha 2: Previsão de entrega com margem */}
                {deliveryWindow && (
                    <div className="flex items-center gap-2">
                        <div className={`flex items-center gap-1.5 text-xs font-black uppercase tracking-widest px-2.5 py-1 rounded-xl border shrink-0 min-w-0 ${
                            Date.now() > deliveryWindow.endMs 
                                ? 'bg-status-critical/20 border-status-critical/50 text-status-critical' 
                                : Date.now() > deliveryWindow.startMs 
                                    ? 'bg-status-warning/20 border-status-warning/50 text-status-warning animate-pulse'
                                    : 'bg-sal-cyber-teal/10 border-sal-cyber-teal/30 text-sal-cyber-teal'
                        }`}>
                            <Clock className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate">Previsão: {deliveryWindow.start} - {deliveryWindow.end}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-6 md:p-8">
                <h3 className="text-3xl font-black text-text-primary mb-2 leading-none tracking-tighter uppercase">{order.customerName}</h3>
                <div className="mb-6">
                    <p className="text-text-secondary text-xl font-medium leading-tight">
                        {order.streetAddress}
                        {order.addressNumber ? `, ${order.addressNumber}` : ''}
                    </p>
                    {(order.complement || order.reference) && (
                        <div className="mt-2 text-base text-text-muted font-bold flex flex-col gap-0.5 border-l-2 border-sal-cyber-teal/30 pl-3">
                            {order.complement && <span className="uppercase tracking-wide">Compl: {order.complement}</span>}
                            {order.reference && <span className="italic opacity-80">Ref: {order.reference}</span>}
                        </div>
                    )}
                    <div className="flex items-center gap-2 mt-4">
                        <div className="p-1.5 bg-sal-cyber-teal/10 rounded-lg"><MapPin className="w-4 h-4 text-sal-cyber-teal" /></div>
                        <p className="font-black text-sal-cyber-teal text-base uppercase tracking-[0.15em]">{order.neighborhood}</p>
                    </div>
                </div>

                {isNext && onStartInternalNav && (
                    <button onClick={onStartInternalNav} className={`w-full mb-6 py-5 rounded-[1.5rem] font-black text-base uppercase tracking-[0.1em] transition-all flex items-center justify-center gap-3 shadow-xl relative overflow-hidden group ${isInternalNavActive ? 'bg-app-bg border-2 border-sal-cyber-teal text-sal-cyber-teal animate-pulse' : 'bg-sal-cyber-teal text-badge-text-inv active:scale-95'}`}>
                        {isInternalNavActive ? (<><Zap className="w-5 h-5 fill-current" /> GPS INTEGRADO ATIVO</>) : (<><Zap className="w-5 h-5 fill-current" /> INICIAR NAVEGAÇÃO S.A.L.</>)}
                    </button>
                )}

                {/* v7.5: LAZY RECON COMPONENT */}
                <div className="mb-6">
                    {reconUrl ? (
                        <div className="rounded-[2rem] overflow-hidden border border-border-dim shadow-xl relative group active:scale-[0.98] transition-all cursor-pointer" onClick={handleLoadFachada}>
                            <img src={reconUrl} alt="Fachada do Local" className="w-full h-40 object-cover brightness-75 group-hover:brightness-100 transition-all duration-500" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex items-end p-5">
                                <div className="flex items-center justify-between w-full">
                                    <span className="text-xs font-black text-badge-text uppercase tracking-widest flex items-center gap-2"><ScanEye className="w-4 h-4 text-sal-cyber-teal" /> Fachada Reconhecida</span>
                                    <div className="p-2 bg-sal-cyber-teal/20 rounded-full border border-sal-cyber-teal/30"><Maximize2 className="w-3 h-3 text-sal-cyber-teal" /></div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={handleLoadFachada}
                            disabled={isLoadingRecon}
                            className="w-full py-5 rounded-3xl bg-black/5 dark:bg-white/5 border-2 border-dashed border-sal-cyber-teal/30 flex flex-col items-center justify-center gap-2 hover:bg-sal-cyber-teal/5 transition-all group"
                        >
                            {isLoadingRecon ? <Loader2 className="w-6 h-6 text-sal-cyber-teal animate-spin" /> : <ScanEye className="w-6 h-6 text-sal-cyber-teal group-hover:scale-110 transition-transform" />}
                            <span className="text-xs font-black text-text-primary uppercase tracking-[0.2em]">S.A.L. RECON: Fachada do Local</span>
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-3 mb-6">
                    <button onClick={() => handleWaze(order.reconLat || order.lat, order.reconLng || order.lng)} className="flex items-center justify-center gap-3 py-5 bg-card-bg border-2 border-status-info/50 text-status-info rounded-3xl font-black text-base uppercase tracking-widest active:bg-status-info active:text-badge-text transition-all shadow-lg">
                        <Navigation className="w-5 h-5 fill-current" /> Waze
                    </button>
                    <button onClick={() => handleMaps(order.reconLat || order.lat, order.reconLng || order.lng, order.address)} className="flex items-center justify-center gap-3 py-5 bg-card-bg border-2 border-border-dim text-text-secondary rounded-3xl font-black text-base uppercase tracking-widest active:bg-black/5 transition-all shadow-lg">
                        <MapPin className="w-5 h-5" /> Maps
                    </button>
                </div>

                <div className="mb-8">
                    {order.phone && order.phone.length > 5 && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-3 gap-3">
                                <button onClick={handleCall} className="flex items-center justify-center gap-2 py-5 bg-status-ready text-badge-text rounded-3xl font-black text-sm uppercase tracking-widest active:scale-95 transition-all shadow-lg"><Phone className="w-5 h-5 fill-current" /> Ligar</button>
                                <button onClick={handleWhatsApp} className="flex items-center justify-center gap-2 py-5 bg-[#25D366] text-white rounded-3xl font-black text-sm uppercase tracking-widest active:scale-95 transition-all shadow-lg"><MessageCircle className="w-5 h-5 fill-current" /> Whats</button>
                                <button onClick={() => onStartHailing(order)} disabled={isProcessingHailing} className="flex items-center justify-center gap-2 py-5 bg-card-bg border-2 border-status-info text-status-info rounded-3xl font-black text-sm uppercase tracking-widest active:scale-95 transition-all shadow-lg disabled:opacity-50 relative group">{isProcessingHailing ? <Loader2 className="w-5 h-5 animate-spin" /> : <BrainCircuit className="w-5 h-5" />} Sofia</button>
                            </div>
                            {isIfoodSystem && (
                                <button
                                    onClick={() => handleIFoodExternalConfirmation(order)}
                                    className="w-full py-4 bg-status-critical/10 border-2 border-status-critical/50 text-status-critical rounded-2xl font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 hover:bg-status-critical hover:text-badge-text transition-all shadow-lg group"
                                >
                                    <CheckCircle2 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                    Validar Entrega no iFood
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {showPaymentInfo && (
                    <div className={`mb-8 p-6 rounded-[2rem] border-2 flex flex-col gap-3 shadow-inner ${isPaid ? 'bg-status-ready/10 border-status-ready/30' : 'bg-status-warning/10 border-status-warning/30'}`}>
                        <div className="flex justify-between items-center">
                            <div className={`text-xs font-black uppercase tracking-[0.2em] ${isPaid ? 'text-status-ready' : 'text-status-warning'}`}>{isPaid ? 'Status do Pagamento' : 'Valor a Receber'}</div>
                            <div className="p-2 bg-black/5 dark:bg-black/20 rounded-xl">{isPaid ? <CreditCard className="w-5 h-5 text-status-ready" /> : <DollarSign className="w-5 h-5 text-status-warning" />}</div>
                        </div>
                        <div className="text-3xl font-black tracking-tighter">{isPaid ? (<span className="text-status-ready flex items-center gap-2 uppercase">Pago Online</span>) : (<span className="text-text-primary font-mono">R$ {order.orderValue?.toFixed(2)}</span>)}</div>
                    </div>
                )}

                <div className="space-y-3">
                    {isIfoodSystem ? (
                        <button
                            type="button"
                            onClick={() => setShowIfoodConfirmation(true)}
                            className="h-16 w-full bg-sal-cyber-teal text-badge-text-inv rounded-full flex items-center justify-center gap-3 px-6 font-black uppercase tracking-[0.16em] text-sm shadow-xl active:scale-[0.98] transition-transform"
                        >
                            <CheckCircle2 className="w-6 h-6" />
                            <span>Finalizar entrega</span>
                        </button>
                    ) : (
                        <TapToConfirm
                            label="Finalizar entrega"
                            title="Confirmar entrega?"
                            description={`Confirmar entrega do pedido ${titleId} para ${order.customerName.split(' ')[0]}.`}
                            confirmLabel="Sim, entregue"
                            successLabel="ENTREGA FINALIZADA!"
                            onConfirm={() => onCompleteOrder(order.id)}
                        />
                    )}
                </div>
            </div>

            {/* iFood Confirmation Overlay */}
            <AnimatePresence>
                {showIfoodConfirmation && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="w-full max-w-sm bg-card-bg rounded-3xl p-6 shadow-2xl flex flex-col gap-4 border border-border-dim"
                        >
                            <div className="flex flex-col items-center text-center">
                                <div className="w-16 h-16 bg-status-critical rounded-full flex items-center justify-center mb-4 shadow-lg shrink-0">
                                    <img src="https://logodownload.org/wp-content/uploads/2017/05/ifood-logo-0.png" alt="iFood" className="w-10 h-10 object-contain invert brightness-0" />
                                </div>
                                <h3 className="text-2xl font-black text-text-primary uppercase mb-2">Validação Obrigatória</h3>
                                <p className="text-base text-text-muted font-medium">Você inseriu o código de 8 dígitos no site do iFood para confirmar a entrega?</p>
                            </div>

                            <div className="p-4 bg-black/5 dark:bg-white/5 rounded-2xl flex flex-col items-center gap-2 border-2 border-border-dim">
                                <span className="text-xs font-bold text-text-muted uppercase tracking-widest">Código de Confirmação</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-3xl font-black text-text-primary font-mono tracking-widest">
                                        {hasCommunicationId ? communicationId : '--------'}
                                    </span>
                                    <button
                                        onClick={() => hasCommunicationId && copyToClipboard(communicationId, 'Código')}
                                        disabled={!hasCommunicationId}
                                        className="p-2 bg-black/10 dark:bg-white/10 rounded-lg active:scale-95 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Copy className="w-4 h-4 text-icon-muted" />
                                    </button>
                                </div>
                                {!hasCommunicationId && (
                                    <span className="text-xs text-status-warning font-bold text-center uppercase tracking-wide">
                                        ID iFood de 8 digitos nao encontrado nas observacoes do pedido
                                    </span>
                                )}
                            </div>

                            <div className="flex flex-col gap-3 mt-2">
                                <button
                                    onClick={() => handleIFoodExternalConfirmation(order)}
                                    className="w-full py-4 bg-status-critical/10 border-2 border-status-critical text-status-critical rounded-xl font-black text-base uppercase tracking-wider active:scale-95 transition-all text-center"
                                >
                                    Abrir iFood Novamente
                                </button>

                                <button
                                    onClick={() => onCompleteOrder(order.id)}
                                    className="w-full py-4 bg-status-ready text-badge-text rounded-xl font-black text-base uppercase tracking-wider shadow-xl active:scale-95 transition-all text-center flex items-center justify-center gap-2"
                                >
                                    <CheckCircle2 className="w-5 h-5" /> Sim, Validado!
                                </button>

                                <button
                                    onClick={() => setShowIfoodConfirmation(false)}
                                    className="w-full py-3 text-text-muted font-bold text-sm uppercase tracking-widest hover:text-text-primary transition-colors"
                                >
                                    Cancelar / Voltar
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};
